import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import client from '../../api/client';
import {
  getAdministrationMenuItems,
  getDashboardPermissions,
  getEinsatzMenuItems,
  getGrundlehrgangSrhtMenuItems,
  canAccessStatistics,
  hasAssessmentCandidateRole,
  isAssessmentObserver,
  getRueckmeldungenMenuItems,
} from '../../config/roleAccess';
import { ROLE_GATE_NOTICE_KEY } from '../../components/roleGateNotice';
import ChangeRequestInbox from '../../components/ChangeRequestInbox';
import { getInboxCount } from '../../api/changeRequests';
import { getMySrhtChecklist } from '../../api/srhtChecklist';

const glassCard = {
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(12px)',
  border: 1,
  borderColor: 'divider',
};

function ProgressRing({ value, target, label, color }) {
  const numericValue = Number(value || 0);
  const numericTarget = Number(target || 0);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  const safeTarget = Number.isFinite(numericTarget) && numericTarget > 0 ? numericTarget : 1;
  const roundedValue = Math.round(safeValue);
  const roundedTarget = Math.round(Number.isFinite(numericTarget) ? numericTarget : 0);
  const pct = Math.min(100, (safeValue / safeTarget) * 100);
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', width: 90, height: 90 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={90}
          thickness={4}
          sx={{ color: 'action.disabledBackground', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={pct}
          size={90}
          thickness={4}
          color={color}
          sx={{ position: 'absolute' }}
        />
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {roundedValue}/{roundedTarget}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
}

export function NavTile({ title, subtitle, icon, accentColor, directPath, menuItems, navigate, disabled = false }) {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = menuItems && menuItems.length > 0;

  useEffect(() => {
    if (disabled && expanded) {
      setExpanded(false);
    }
  }, [disabled, expanded]);

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (directPath) {
      navigate(directPath);
    } else {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <Card
      sx={{
        ...glassCard,
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: disabled ? 0.58 : 1,
        filter: disabled ? 'saturate(0.6)' : 'none',
      }}
    >
      <CardActionArea
        onClick={handleClick}
        disabled={disabled}
        sx={{
          touchAction: 'manipulation',
          display: 'flex',
          alignItems: 'stretch',
          flexGrow: 1,
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, pl: 0, width: '100%', minHeight: { xs: 112, sm: 124, lg: 132 } }}>
          <Box sx={{ width: 4, alignSelf: 'stretch', bgcolor: accentColor, borderRadius: 1, flexShrink: 0, ml: 0 }} />
          <Box sx={{ color: accentColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { fontSize: 32 } })}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
            {subtitle ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.25,
                  minHeight: '2.6em',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {isExpandable ? (
            <ExpandMoreIcon sx={{
              transition: 'transform 300ms',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'text.secondary',
              mr: 1,
            }} />
          ) : (
            <ArrowForwardIcon sx={{ color: 'text.secondary', mr: 1 }} />
          )}
        </CardContent>
      </CardActionArea>
      {isExpandable && (
        <Collapse in={expanded} timeout={300}>
          <List disablePadding sx={{ pl: { xs: 2.5, sm: 5 }, pr: 1.5, pb: 1 }}>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.path}
                onClick={item.disabled ? undefined : () => navigate(item.path)}
                disabled={Boolean(item.disabled)}
                dense
                sx={{ alignItems: 'flex-start' }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ variant: 'body2', sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Card>
  );
}

function DashboardPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [requirements, setRequirements] = useState({ total: 72, trainer: 54 });
  const [pendingChangeRequestCount, setPendingChangeRequestCount] = useState(0);
  const [changeRequestDialogOpen, setChangeRequestDialogOpen] = useState(false);
  const [showSrhtChecklistAction, setShowSrhtChecklistAction] = useState(false);
  const currentYear = new Date().getFullYear();
  const showEquipmentInspectorHours = Array.isArray(user?.roles) && user.roles.includes('geräteprüfer');
  const permissions = useMemo(() => getDashboardPermissions(user) || {}, [user]);
  const showAdministrationOnly = Boolean(permissions.showAdministrationOnly);
  const showDashboardGreetingOnly = Boolean(permissions.showDashboardGreetingOnly);
  const canViewLogbook = Boolean(permissions.logbuchAnsicht);
  const hideHourOverview = showDashboardGreetingOnly || hasAssessmentCandidateRole(user) || isAssessmentObserver(user);
  const rueckmeldungenMenuItems = useMemo(() => getRueckmeldungenMenuItems(user) || [], [user]);
  const administrationMenuItems = useMemo(() => getAdministrationMenuItems(user) || [], [user]);
  const grundlehrgangSrhtMenuItems = useMemo(() => getGrundlehrgangSrhtMenuItems(user) || [], [user]);
  const roleGateNotice = location.state?.[ROLE_GATE_NOTICE_KEY] || null;

  useEffect(() => {
    client.get(`/api/training/my-hours?year=${currentYear}`)
      .then((res) => {
        setStats({
          total_hours: res.data.stats?.total_hours || 0,
          trainer_hours: res.data.stats?.trainer_hours || 0,
          equipment_inspector_hours: res.data.stats?.equipment_inspector_hours || 0,
        });
        setRequirements(res.data.requirements || { total: 72, trainer: 54 });
      })
      .catch(() => {
        setStats({ total_hours: 0, trainer_hours: 0, equipment_inspector_hours: 0 });
        setRequirements({ total: 72, trainer: 54 });
      });
  }, [currentYear]);

  const loadPendingChangeRequestCount = useCallback(async () => {
    try {
      const response = await getInboxCount();
      setPendingChangeRequestCount(Number(response.data?.pendingCount || 0));
    } catch {
      setPendingChangeRequestCount(0);
    }
  }, []);

  useEffect(() => {
    if (!canViewLogbook) {
      setPendingChangeRequestCount(0);
      return undefined;
    }

    loadPendingChangeRequestCount();
    const intervalId = setInterval(loadPendingChangeRequestCount, 30000);
    return () => clearInterval(intervalId);
  }, [canViewLogbook, loadPendingChangeRequestCount]);

  useEffect(() => {
    if (!canViewLogbook) {
      setShowSrhtChecklistAction(false);
      return;
    }

    let active = true;
    getMySrhtChecklist()
      .then((response) => {
        if (!active) {
          return;
        }

        const status = String(response.data?.checklist?.status || '').trim();
        setShowSrhtChecklistAction(Boolean(response.data?.checklist) && status !== 'finalized');
      })
      .catch(() => {
        if (active) {
          setShowSrhtChecklistAction(false);
        }
      });

    return () => {
      active = false;
    };
  }, [canViewLogbook]);

  const formatHours = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return String(Math.round(numericValue));
  };

  const displayName = useMemo(() => {
    const rawName = user?.fullName || user?.full_name;
    if (rawName) {
      const parts = rawName.split(',');
      const name = parts.length > 1 ? parts[1].trim() : parts[0].trim();
      if (name) return name;
    }
    return user?.username || '';
  }, [user?.fullName, user?.full_name, user?.username]);

  const today = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 6) return 'Guten Abend';
    if (hour < 11) return 'Guten Morgen';
    if (hour < 14) return 'Mahlzeit';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  }, []);

  const tiles = useMemo(() => [
    {
      id: 'einsatz',
      title: 'Einsatz',
      subtitle: 'Rettungsvarianten & Handlungsempfehlungen',
      icon: <LocalFireDepartmentIcon />,
      accentColor: '#E67E22',
      menuItems: getEinsatzMenuItems(user),
      visible: permissions.showEinsatz,
    },
    {
      id: 'uebung',
      title: 'Übungsplanung',
      subtitle: 'Übungen planen und verwalten',
      icon: <CalendarMonthIcon />,
      accentColor: '#2980B9',
      menuItems: [
        { label: 'Übung anlegen', path: '/uebung?type=anlegen' },
        { label: 'Teilnehmer nachtragen', path: '/uebung?type=teilnehmer' },
        { label: 'Stadtgebiet Düsseldorf?', path: '/uebung?type=stadtgebiet' },
        { label: 'Öffentlichkeitswirksam?', path: '/uebung?type=oeffentlichkeit' },
      ],
      disabled: true,
      visible: permissions.showUebungsplanung,
    },
    {
      id: 'rueckmeldung',
      title: 'Rückmeldungen',
      subtitle: 'Formularmeldungen unterschiedlicher Belange',
      icon: <FeedbackIcon />,
      accentColor: '#8E44AD',
      menuItems: rueckmeldungenMenuItems,
      visible: permissions.showRueckmeldungen,
    },
    {
      id: 'grundlehrgang-srht',
      title: 'Grundlehrgang SRHT',
      subtitle: 'Eignungsverfahren, Lehrgang und Prüfung',
      icon: <SchoolIcon />,
      accentColor: '#16A085',
      menuItems: grundlehrgangSrhtMenuItems,
      visible: grundlehrgangSrhtMenuItems.length > 0,
    },
    {
      id: 'administration',
      title: 'Administration',
      subtitle: 'System- und Benutzerverwaltung',
      icon: <AdminPanelSettingsIcon />,
      accentColor: '#6C7A89',
      menuItems: administrationMenuItems,
      visible: administrationMenuItems.length > 0,
    },
  ], [administrationMenuItems, grundlehrgangSrhtMenuItems, permissions, rueckmeldungenMenuItems, user]);

  const visibleTiles = tiles.filter((t) => {
    if (!t.visible) {
      return false;
    }

    if (showAdministrationOnly) {
      return t.id === 'administration';
    }

    return true;
  });

  const dismissRoleGateNotice = () => {
    const nextState = { ...(location.state || {}) };
    delete nextState[ROLE_GATE_NOTICE_KEY];

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : null,
    });
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 3, md: 5 }, py: { xs: 1.5, md: 3 } }}>
      {roleGateNotice ? (
        <Alert severity={roleGateNotice.severity || 'warning'} onClose={dismissRoleGateNotice} sx={{ mb: 2 }}>
          {roleGateNotice.message}
        </Alert>
      ) : null}

      {/* Zone 1: Training Progress Hero */}
      <Card sx={{ ...glassCard, mb: 2, p: { xs: 1.5, sm: 2.5, md: 3.5 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
        }}>
          {/* Left: Greeting */}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {greeting}, {displayName || 'Nutzer'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {today} &mdash; Ausbildungsjahr {currentYear}
            </Typography>
            {canViewLogbook && pendingChangeRequestCount > 0 ? (
              <Chip
                label={`${pendingChangeRequestCount} offene Änderungsanfragen`}
                color="warning"
                variant="filled"
                onClick={() => setChangeRequestDialogOpen(true)}
                clickable
                sx={{ mt: 1.25, fontWeight: 700 }}
              />
            ) : null}
          </Box>

          {/* Right: Progress Rings */}
          {!hideHourOverview ? (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, sm: 1.5, md: 3 },
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <ProgressRing
                value={stats?.total_hours || 0}
                target={requirements.total || 72}
                label="Gesamtstunden"
                color="primary"
              />
              <ProgressRing
                value={stats?.trainer_hours || 0}
                target={requirements.trainer || 54}
                label="Lehrhöhenretter"
                color="success"
              />
              {showEquipmentInspectorHours ? (
                <Box
                  sx={{
                    minWidth: { xs: '100%', sm: 140, md: 132 },
                    flexBasis: { xs: '100%', sm: 'auto', md: 'auto' },
                    px: 1.5,
                    py: 1.25,
                    minHeight: 96,
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'divider',
                    backgroundColor: 'rgba(255,255,255,0.72)',
                    order: { xs: 3, sm: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.1, lineHeight: 1.1 }}
                  >
                    Geräteprüfer
                    <br />
                    stunden
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1, mt: 0.5 }}>
                    {formatHours(stats?.equipment_inspector_hours || 0)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        {/* Bottom: Action Buttons */}
        {!showDashboardGreetingOnly ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
          {permissions.logbuchEintragVornehmen && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/eintrag-vornehmen')}
            >
              Eintrag hinzufügen
            </Button>
          )}
          {permissions.logbuchAnsicht && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MenuBookIcon />}
                onClick={() => navigate('/logbuch')}
              >
                Logbuch öffnen
              </Button>
            </>
          )}
          {canAccessStatistics(user) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<BarChartIcon />}
              onClick={() => navigate('/statistik')}
            >
              Statistik
            </Button>
          )}
          {showSrhtChecklistAction ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ChecklistIcon />}
              onClick={() => navigate('/laufzettel-srht')}
            >
              Laufzettel
            </Button>
          ) : null}
          </Box>
        ) : null}
      </Card>

      {/* Zone 2: Navigation Tiles */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          alignItems: 'start',
        }}
      >
        {visibleTiles.map((tile) => (
          <Box key={tile.id} sx={{ minWidth: 0, display: 'flex' }}>
            <NavTile
              title={tile.title}
              subtitle={tile.subtitle}
              icon={tile.icon}
              accentColor={tile.accentColor}
              directPath={tile.directPath}
              menuItems={tile.menuItems}
              navigate={navigate}
              disabled={Boolean(tile.disabled)}
            />
          </Box>
        ))}
      </Box>

      <Dialog
        open={changeRequestDialogOpen}
        onClose={() => setChangeRequestDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Offene Änderungsanfragen</DialogTitle>
        <DialogContent dividers>
          <ChangeRequestInbox
            embedded
            showWhenEmpty
            title=""
            onCountChange={setPendingChangeRequestCount}
            onResolved={loadPendingChangeRequestCount}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default DashboardPage;
